import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, Image, TouchableOpacity, Modal, Pressable, Animated, StyleSheet, Platform } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { getAuth, signOut, signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { globalStyles } from '../styles/globalStyles';
import { useUser } from '../components/LoginPersistence';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { BREAKPOINTS } from '../styles/breakpoints';
import { HideOnScroll } from './HideOnScroll';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import CommunityModal from './CommunityModal';

const USER_TYPES = {
  ADMIN: {
    label: 'Admin',
    value: 'admin',
    color: 'pink'
  },
  MEMBER: {
    label: 'Member',
    value: 'member',
    color: '#0bb5df'
  },
  VENUE_EMPLOYEE: {
    label: 'Venue Employee',
    value: 'venue_employee',
    color: '#2ecc71'
  },
  VENUE_OWNER: {
    label: 'Venue Owner',
    value: 'venue_owner',
    color: '#9b59b6'
  },
  ARTIST: {
    label: 'Artist',
    value: 'artist',
    color: '#e67e22'
  },
  BAND_MEMBER: {
    label: 'Band Member',
    value: 'band_member',
    color: '#f1c40f'
  },
  MUSIC_INDUSTRY: {
    label: 'Music Industry Employee',
    value: 'music_industry',
    color: '#1abc9c'
  }
};

export function HeaderBar() {
  const { user, loading } = useUser();
  const [menuVisible, setMenuVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const navigation = useNavigation();
  const route = useRoute();
  const auth = getAuth();
  const db = getFirestore();
  const menuAnimation = useRef(new Animated.Value(0)).current;

  // Function to get header title color based on route
  const getHeaderTitleColor = () => {
    const routeName = route?.name;
    if (routeName === 'CommunityList' || routeName === 'CommunityScreen') {
      return '#feffe4';
    }
    return '#3ba378';
  };
  const mobileMenuAnimation = useRef(new Animated.Value(0)).current;
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Unread count and owned organizations (needed for header badge)
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [ownedOrganizations, setOwnedOrganizations] = useState([]);
  
  // Community Modal Context
  const {
    showModal: showActivityLog, // Renamed for existing usage
    setShowModal,
    activeTab,
    activeDiscussionTab,
    messagingTargetUserId,
    messagingExistingConversationId,
    openModal,
    openMessaging,
    closeModal,
    setActiveTab,
    setActiveDiscussionTab
  } = useCommunityModal();



  // Check if we're on mobile or tablet
  useEffect(() => {
    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        setIsMobile(width <= BREAKPOINTS.mobile);
        setIsTablet(width > BREAKPOINTS.mobile && width <= BREAKPOINTS.tablet);
      }
    };
    
    checkScreenSize();
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, []);

  useEffect(() => {
    if (menuVisible) {
      Animated.timing(menuAnimation, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true
      }).start();
    } else {
      Animated.timing(menuAnimation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true
      }).start();
    }
  }, [menuVisible]);

  useEffect(() => {
    if (mobileMenuVisible) {
      Animated.timing(mobileMenuAnimation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true
      }).start();
    } else {
      Animated.timing(mobileMenuAnimation, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true
      }).start();
    }
  }, [mobileMenuVisible]);

  // Single useEffect to handle user data
  useEffect(() => {
    if (!loading && user) {
      setIsAdmin(user.userType === 'admin');
      setUserName(user.username || user.firstName || 'Guest');
      
      // Validate and set photoURL
      if (user.photoURL && typeof user.photoURL === 'string' && user.photoURL.startsWith('http')) {
        setPhotoURL(user.photoURL);
      } else {
        setPhotoURL('');
      }
    } else if (!loading) {
      setIsAdmin(false);
      setUserName('Guest');
      setPhotoURL('');
    }
  }, [user, loading]);

  // Add a separate effect to monitor photoURL changes
  useEffect(() => {
    console.log('Current photoURL state:', photoURL);
  }, [photoURL]);

  // Load organizations owned by the user (needed for unread count calculation)
  const loadOwnedOrganizations = useCallback(async () => {
    if (!user?.uid) {
      setOwnedOrganizations([]);
      return;
    }
    try {
      const orgsQuery = query(
        collection(db, 'organizations'),
        where('ownerId', '==', user.uid)
      );
      const snapshot = await getDocs(orgsQuery);
      const orgs = [];
      snapshot.forEach(doc => {
        orgs.push({ id: doc.id, ...doc.data() });
      });
      setOwnedOrganizations(orgs);
    } catch (error) {
      console.error('Error loading owned organizations:', error);
      setOwnedOrganizations([]);
    }
  }, [user?.uid, db]);

  useEffect(() => {
    loadOwnedOrganizations();
  }, [loadOwnedOrganizations]);

  // Listen for unread message counts in real-time
  // Calculate unread for both user and all owned organizations
  // Also include unanswered organization invitations
  useEffect(() => {
    if (!user?.uid || !user?.email) {
      setTotalUnreadCount(0);
      return;
    }

    // Get all IDs to check unread counts for (user + all owned organizations)
    const allSenderIds = [user.uid, ...ownedOrganizations.map(org => org.id)];
    
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains-any', allSenderIds)
    );

    // Query for pending organization invitations for this user
    const invitationsQuery = query(
      collection(db, 'organization_invitations'),
      where('inviteeEmail', '==', user.email),
      where('status', '==', 'pending')
    );

    let conversationUnread = 0;
    let invitationCount = 0;

    const unsubscribeConversations = onSnapshot(conversationsQuery, (snapshot) => {
      conversationUnread = 0;
      
      snapshot.docs.forEach(doc => {
        const conversationData = doc.data();
        // Sum unread counts for user and all owned organizations
        allSenderIds.forEach(senderId => {
          const unreadCount = conversationData.unreadCounts?.[senderId] || 0;
          conversationUnread += unreadCount;
        });
      });
      
      // Update total with both conversation unread and invitation count
      setTotalUnreadCount(conversationUnread + invitationCount);
    }, (error) => {
      console.error('🔔 Error listening to conversations:', error);
      setTotalUnreadCount(invitationCount); // Still show invitation count even if conversations fail
    });

    const unsubscribeInvitations = onSnapshot(invitationsQuery, (snapshot) => {
      invitationCount = snapshot.size;
      
      // Update total with both conversation unread and invitation count
      setTotalUnreadCount(conversationUnread + invitationCount);
    }, (error) => {
      console.error('🔔 Error listening to organization invitations:', error);
      setTotalUnreadCount(conversationUnread); // Still show conversation count even if invitations fail
    });

    return () => {
      unsubscribeConversations();
      unsubscribeInvitations();
    };
  }, [user?.uid, user?.email, ownedOrganizations]);

  // Add a separate effect to monitor photoURL changes
  useEffect(() => {
    console.log('Current photoURL state:', photoURL);
  }, [photoURL]);

  // Format unread count for display
  const formatUnreadCount = (count) => {
    if (count === 0) return null;
    if (count >= 100) return '99+';
    return count.toString();
  };

  const handleSignOut = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        localStorage.removeItem('devModeUser');
      }
      await signOut(auth);
      navigation.replace('Home');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleLoginClick = () => {
    navigation.navigate('Login');
  };

  const handleSignUpClick = () => {
    navigation.navigate('Login', { isSignUp: true });
  };

  const isDevelopment = process.env.NODE_ENV === 'development';

  const menuItems = user ? [
    { title: 'Search Venues', onPress: () => navigation.navigate('VenueList') },
    { title: 'Add a Venue', onPress: () => navigation.navigate('AddVenue') },
    { title: 'Community Directory', onPress: () => navigation.navigate('CommunityScreen') },
    // Admin-specific items can be inserted here in the future without duplicating entries
    { title: 'My Account', onPress: () => navigation.navigate('Profile') },
    ...((isAdmin || isDevelopment) ? [{ title: 'Admin Panel', onPress: () => navigation.navigate('Admin') }] : []),
  ] : [
    { title: 'Search Venues', onPress: () => navigation.navigate('VenueList') },
  ];

  // Add hover effect to menu items
  const menuItemHoverStyle = {
    color: user ? (USER_TYPES[user?.userType?.toUpperCase()]?.color || '#0bb5df') : '#0bb5df',
  };

  if (loading) {
    return (
      <View style={globalStyles.headerContainer}>
        <View style={globalStyles.headerLeft}>
          <Image
            source={require('../../assets/logo.png')}
            resizeMode="contain"
            style={globalStyles.headerLogo}
          />
          <View style={{ flexDirection: 'column', alignItems: 'flex-start', marginLeft: 5 }}>
            <Text style={[globalStyles.headerTitle, { color: getHeaderTitleColor() }]}>
              Venue Report Card
            </Text>
            <Text style={globalStyles.headerWelcome}>Loading...</Text>
          </View>
        </View>
      </View>
    );
  }

  // Hamburger menu icon component
  const HamburgerIcon = () => (
    <TouchableOpacity 
      style={globalStyles.hamburgerButton}
      onPress={() => setMobileMenuVisible(true)}
    >
      <View style={globalStyles.hamburgerLine} />
      <View style={globalStyles.hamburgerLine} />
      <View style={globalStyles.hamburgerLine} />
    </TouchableOpacity>
  );

  return (
    <HideOnScroll style={globalStyles.headerContainer}>
      <View style={globalStyles.headerLeft}>
        <Pressable onPress={() => navigation.navigate('Home')}>
          <Image
            source={require('../../assets/logo.png')}
            resizeMode="contain"
            style={globalStyles.headerLogo}
          />
        </Pressable>
        <View style={{ flexDirection: 'column', alignItems: 'flex-start', marginLeft: 5 }}>
          <Text style={[globalStyles.headerTitle, { color: getHeaderTitleColor() }]}>
            Venue Report Card
          </Text>
          {!isMobile && (
            <Text style={[globalStyles.headerWelcome, { fontWeight: '100' }]}>
              Welcome, {user ? (userName || 'Guest') : 'Guest'}
            </Text>
          )}
        </View>
      </View>

      {(isMobile || isTablet) ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
          {/* Community Bell for Mobile */}
          {user && (
            <TouchableOpacity
              style={[globalStyles.activityBell, { position: 'relative' }]}
              onPress={() => showActivityLog ? closeModal() : openModal()}
            >
              <Image source={require('../../assets/icons/group.png')} style={{ width: 20, height: 20 }} />
              
              {/* Unread Messages Badge */}
              {formatUnreadCount(totalUnreadCount) && (
                <View style={headerUnreadBadgeStyle}>
                  <Text style={headerUnreadTextStyle}>
                    {formatUnreadCount(totalUnreadCount)}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          )}
          <HamburgerIcon />
        </View>
      ) : (
        user ? (
          <View style={globalStyles.headerRightSection}>
            {/* Community Bell */}
            <TouchableOpacity
              style={[globalStyles.activityBell, { position: 'relative' }]}
              onPress={() => showActivityLog ? closeModal() : openModal()}
            >
              <Image source={require('../../assets/icons/group.png')} style={{ width: 24, height: 24 }} />
              
              {/* Unread Messages Badge */}
              {formatUnreadCount(totalUnreadCount) && (
                <View style={headerUnreadBadgeStyle}>
                  <Text style={headerUnreadTextStyle}>
                    {formatUnreadCount(totalUnreadCount)}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
            
            {/* Profile Picture */}
            <View style={globalStyles.headerProfile}>
              <TouchableOpacity
                onPress={() => setMenuVisible(true)}
                onMouseEnter={() => setMenuVisible(true)}
                style={{ width: '100%', height: '100%', position: 'relative' }}
              >
                <Image
                  source={
                    photoURL
                      ? { uri: photoURL }
                      : require('../../assets/profile_photos/koi.png')
                  }
                  resizeMode="cover"
                  style={globalStyles.headerProfileImage}
                />
              </TouchableOpacity>
              {user?.verificationStatus === 'approved' && (
                <VerifiedBadge style={[
                  globalStyles.verifiedBadge,
                  { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }
                ]} />
              )}
            </View>
          </View>
        ) : (
          <View style={globalStyles.authButtonsContainer}>
            <TouchableOpacity
              style={globalStyles.loginButton}
              onPress={handleLoginClick}
            >
              <Text style={globalStyles.loginButtonText}>Login</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={globalStyles.signupButton}
              onPress={handleSignUpClick}
            >
              <Text style={globalStyles.signupButtonText}>Sign Up</Text>
            </TouchableOpacity>
          </View>
        )
      )}

      {/* Community Modal */}
      <CommunityModal />

      {/* Desktop dropdown menu */}
      <Modal
        visible={menuVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setMenuVisible(false)}
      >
        <Pressable
          style={[globalStyles.modalOverlay, { outline: 'none' }]}
          onPress={() => setMenuVisible(false)}
          onMouseLeave={() => setMenuVisible(false)}
        >
          <Animated.View 
            style={[
              globalStyles.menuContainer,
              { 
                height: 'auto',
                marginTop: 0,
                opacity: menuAnimation,
                transform: [
                  {
                    scaleX: menuAnimation
                  },
                  {
                    scaleY: menuAnimation.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0.15, 1]
                    })
                  }
                ]
              }
            ]}
          >
            <View style={[globalStyles.menuHeader]}>
              <Text style={[globalStyles.menuUsername]}>
                {userName || 'Guest'}
              </Text>
              <View style={{ position: 'relative' }}>
                <Image
                  source={
                    photoURL
                      ? { uri: photoURL }
                      : require('../../assets/profile_photos/koi.png')
                  }
                  onError={(e) => console.error('Menu image loading error:', e.nativeEvent)}
                  style={globalStyles.menuProfileImage}
                />
                {user?.verificationStatus === 'approved' && (
                  <VerifiedBadge style={[
                    globalStyles.verifiedBadge,
                    { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }
                  ]} />
                )}
              </View>
            </View>

            <Animated.View style={[globalStyles.menuItems, { opacity: menuAnimation }]}>
              {menuItems.map((item, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    globalStyles.menuItem,
                    hoveredItem === index && { 
                      transform: 'translateX(5px)',
                      transition: 'all 0.3s ease'
                    },
                  ]}
                  onPress={() => {
                    setMenuVisible(false);
                    item.onPress();
                  }}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Text style={[
                    globalStyles.menuItemText,
                    (item.title === 'Admin Panel' || hoveredItem === index) && { 
                      color: user ? (USER_TYPES[user?.userType?.toUpperCase()]?.color || '#0bb5df') : '#0bb5df',
                      fontWeight: '600'
                    }
                  ]}>
                    {item.title}
                  </Text>
                </TouchableOpacity>
              ))}
            </Animated.View>

            <TouchableOpacity
              style={[
                globalStyles.signOutButton,
                { backgroundColor: '#000000' }
              ]}
              onPress={handleSignOut}
            >
              <Text style={[globalStyles.signOutText, { color: '#ffffff' }]}>Sign Out</Text>
            </TouchableOpacity>
          </Animated.View>
        </Pressable>
      </Modal>

      {/* Mobile menu */}
      <Modal
        visible={mobileMenuVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setMobileMenuVisible(false)}
      >
        <Animated.View 
          style={[
            globalStyles.mobileMenuContainer,
            {
              opacity: mobileMenuAnimation,
              transform: [
                {
                  translateX: mobileMenuAnimation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [300, 0]
                  })
                }
              ]
            }
          ]}
        >
          <View style={globalStyles.mobileMenuHeader}>
            <TouchableOpacity 
              style={globalStyles.closeButton}
              onPress={() => setMobileMenuVisible(false)}
            >
              <Text style={globalStyles.closeButtonText}>✕</Text>
            </TouchableOpacity>
            {user ? (
              <View style={globalStyles.mobileProfileSection}>
                <Image
                  source={
                    photoURL
                      ? { uri: photoURL }
                      : require('../../assets/profile_photos/koi.png')
                  }
                  style={globalStyles.mobileProfileImage}
                />
                <Text style={globalStyles.mobileUsername}>{userName || 'Guest'}</Text>
                {user?.verificationStatus === 'approved' && (
                  <VerifiedBadge style={[
                    globalStyles.mobileVerifiedBadge,
                    { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }
                  ]} />
                )}
              </View>
            ) : (
              <View style={globalStyles.mobileProfileSection}>
                <Text style={globalStyles.mobileUsername}>Guest</Text>
              </View>
            )}
          </View>

          <View style={globalStyles.mobileMenuItems}>
            <TouchableOpacity
              style={globalStyles.mobileMenuItem}
              onPress={() => {
                setMobileMenuVisible(false);
                navigation.navigate('Home');
              }}
            >
              <Text style={globalStyles.mobileMenuItemText}>Home</Text>
            </TouchableOpacity>
            
            {menuItems.map((item, index) => (
              <TouchableOpacity
                key={index}
                style={globalStyles.mobileMenuItem}
                onPress={() => {
                  setMobileMenuVisible(false);
                  item.onPress();
                }}
              >
                <Text style={globalStyles.mobileMenuItemText}>{item.title}</Text>
              </TouchableOpacity>
            ))}
            
            {user ? (
              <TouchableOpacity
                style={[
                  globalStyles.mobileSignOutButton,
                  { backgroundColor: '#000000' }
                ]}
                onPress={() => {
                  setMobileMenuVisible(false);
                  handleSignOut();
                }}
              >
                <Text style={[globalStyles.mobileSignOutText, { color: '#ffffff' }]}>Sign Out</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  globalStyles.mobileSignOutButton,
                  { backgroundColor: '#0bb5df' }
                ]}
                onPress={() => {
                  setMobileMenuVisible(false);
                  handleLoginClick();
                }}
              >
                <Text style={[globalStyles.mobileSignOutText, { color: '#ffffff' }]}>Login</Text>
              </TouchableOpacity>
            )}
            
            <TouchableOpacity
              style={[
                globalStyles.mobileSignOutButton,
                { 
                  backgroundColor: 'transparent',
                  borderWidth: 1,
                  borderColor: '#0bb5df',
                  marginTop: 10
                }
              ]}
              onPress={() => {
                setMobileMenuVisible(false);
                handleSignUpClick();
              }}
            >
              <Text style={[globalStyles.mobileSignOutText, { color: '#0bb5df' }]}>Sign Up</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
        
        <Pressable
          style={[
            globalStyles.mobileMenuOverlay,
            {
              opacity: mobileMenuAnimation.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 0.5]
              })
            }
          ]}
          onPress={() => setMobileMenuVisible(false)}
        />
      </Modal>
    </HideOnScroll>
  );
}

// Header Unread Badge Styles (for header bell icon)
const headerUnreadBadgeStyle = {
  position: 'absolute',
  top: -8,
  right: -8,
  backgroundColor: '#e74c3c',
  borderRadius: 10,
  minWidth: 20,
  height: 20,
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: 6,
  borderWidth: 2,
  borderColor: '#ffffff',
};

const headerUnreadTextStyle = {
  color: '#ffffff',
  fontSize: 11,
  fontFamily: 'VastagoGrotesk',
  fontWeight: 'bold',
};